import React, { useState, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import Logo from './logo/hirefly-logo.png';
import { db, auth } from './firebase-config';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, query, where, serverTimestamp, setDoc, deleteDoc, arrayUnion } from 'firebase/firestore';
import './EmployeeFeed.css';
import './JobCard.css';
import { Link } from 'react-router-dom';
import LetterAvatar from './LetterAvatar';
import LoadingSpinner from './LoadingSpinner';
import { FaRegHandPointer, FaBookmark } from 'react-icons/fa';
import { fetchAdzunaJobs } from '../services/adzunaService';

const DraggableCard = ({ children, onSwipeComplete, onRightSwipe, onLeftSwipe, className }) => {
  const [{ x }, api] = useSpring(() => ({
    x: 0,
    config: { tension: 200, friction: 20 }
  }));

  const bind = useGesture({
    onDrag: ({ down, movement: [mx], velocity }) => {
      const swipeThreshold = 150;

      if (!down && (Math.abs(mx) > swipeThreshold || velocity > 0.3)) {
        const direction = mx > 0 ? 1 : -1;
        if (direction === 1) {
          onRightSwipe();
        } else {
          onLeftSwipe();
        }
        api.start({ x: direction * window.innerWidth, onRest: () => onSwipeComplete(() => api.start({ x: 0 })) });
      } else if (down) {
        api.start({ x: mx });
      } else {
        api.start({ x: 0 });
      }
    }
  });

  return (
    <animated.div {...bind()} style={{ x }} className={`job-card ${className || ''}`}>
      {children}
    </animated.div>
  );
};

const EmployeeFeed = () => {
  const navigate = useNavigate();
  const [userProfilePicture, setUserProfilePicture] = useState('');
  const [userName, setUserName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [index, setIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [fadeOutConfetti, setFadeOutConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isLoading, setIsLoading] = useState(true);
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [declinedJobs, setDeclinedJobs] = useState(new Set());
  const [activeFilter, setActiveFilter] = useState(null);
  const [activeJobCardTab, setActiveJobCardTab] = useState('Description');
  const [selectedFilters, setSelectedFilters] = useState({
    jobType: null,
    location: null,
    compensation: null,
    industry: null
  });
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [modalDescription, setModalDescription] = useState('');
  const [modalRequirements, setModalRequirements] = useState('');
  const [showEmployerModal, setShowEmployerModal] = useState(false);
  const [employerDetails, setEmployerDetails] = useState(null);
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [bookmarkPop, setBookmarkPop] = useState(false);
  const [employerModalTab, setEmployerModalTab] = useState('jobs');
  const [employerJobs, setEmployerJobs] = useState([]);
  const [showExternalJobs, setShowExternalJobs] = useState(() => {
    const saved = localStorage.getItem('showExternalJobs');
    return saved !== null ? saved === 'true' : false;
  });
  const [showRemoteJobs, setShowRemoteJobs] = useState(false);
  const [listMode, setListMode] = useState(() => {
    const saved = localStorage.getItem('listMode');
    return saved !== null ? saved === 'true' : false;
  });
  const [externalJobs, setExternalJobs] = useState([]);
  const [isLoadingExternal, setIsLoadingExternal] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [modalJob, setModalJob] = useState(null);
  const [showExternalApplyConfirm, setShowExternalApplyConfirm] = useState(false);
  const [pendingExternalJob, setPendingExternalJob] = useState(null);
  const [showSimilarRoles, setShowSimilarRoles] = useState(false);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [companyDescription, setCompanyDescription] = useState('');
  const [isCompanyInfoLoading, setIsCompanyInfoLoading] = useState(false);

  const filterOptions = {
    jobType: ['Full-time', 'Part-time', 'Contract', 'Internship', 'Remote', 'Temporary', 'Volunteer', 'Seasonal', 'Commission', 'Per Diem'],
    location: ['Remote', 'On-site', 'Hybrid', 'International', 'Local', 'Flexible', 'Multiple Cities'],
    compensation: ['$0-50k', '$50k-100k', '$100k-150k', '$150k+', 'Equity', 'Hourly', 'Commission', 'Bonus'],
    industry: ['Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 'Manufacturing', 'Other', 'Legal', 'Hospitality', 'Government', 'Non-profit', 'Construction', 'Transportation', 'Energy', 'Media']
  };

  const filterTypes = [
    { type: 'jobType', label: 'Job Type' },
    { type: 'location', label: 'Location' },
    { type: 'compensation', label: 'Compensation' },
    { type: 'industry', label: 'Industry' },
    { type: 'experience', label: 'Experience' },
    { type: 'education', label: 'Education' },
    { type: 'companySize', label: 'Company Size' },
    { type: 'perks', label: 'Perks' },
    { type: 'language', label: 'Language' },
    { type: 'remotePolicy', label: 'Remote Policy' }
  ];

  const handleFilterClick = (filterType) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
  };

  const handleFilterSelect = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setActiveFilter(null);
  };

  const clearFilter = (filterType) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: null
    }));
  };

  const filterJobs = (jobsToFilter) => {
    let filtered = [...jobsToFilter];

    // Helper: extract a comparable numeric salary from various formats
    const parseNumericSalary = (salaryValue) => {
      if (!salaryValue) return 0;
      if (typeof salaryValue === 'number') return salaryValue;
      const str = String(salaryValue);
      const numberMatches = str.match(/\$?\s*([0-9]{2,3}[,0-9]*)/g);
      if (!numberMatches || numberMatches.length === 0) return 0;
      const nums = numberMatches.map(m => Number(m.replace(/[^0-9]/g, ''))).filter(n => !isNaN(n));
      if (nums.length === 0) return 0;
      if (nums.length === 1) return nums[0];
      // Average the range if two or more numbers given
      const sum = nums.reduce((a, b) => a + b, 0);
      return Math.round(sum / nums.length);
    };

    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job => {
        const searchableText = [
          job.title,
          job.company,
          job.description,
          job.location,
          job.requirements,
          ...(job.reasonsToJoin || [])
        ].join(' ').toLowerCase();
        return searchableText.includes(query);
      });
    }

    // Apply selected button filters (jobType, location, compensation, industry)
    const { jobType, location, compensation, industry } = selectedFilters;
    if (jobType) {
      const jt = jobType.toLowerCase();
      filtered = filtered.filter(job =>
        [job.jobType, job.title].filter(Boolean).some(v => String(v).toLowerCase().includes(jt))
      );
    }
    if (location) {
      const loc = location.toLowerCase();
      filtered = filtered.filter(job => {
        const hay = [job.location, job.jobType].filter(Boolean).join(' ').toLowerCase();
        if (loc === 'remote') return hay.includes('remote');
        if (loc === 'hybrid') return hay.includes('hybrid');
        return hay.includes(loc);
      });
    }
    if (compensation) {
      filtered = filtered.filter(job => {
        const numeric = parseNumericSalary(job.salary);
        if (!numeric) return false;
        return matchSalaryRange(numeric, compensation);
      });
    }
    if (industry) {
      const ind = industry.toLowerCase();
      filtered = filtered.filter(job =>
        [job.industry, job.title, job.company].filter(Boolean).some(v => String(v).toLowerCase().includes(ind))
      );
    }

    // Apply toggle filters
    if (showRemoteJobs) {
      filtered = filtered.filter(job => 
        job.location?.toLowerCase().includes('remote') || 
        job.jobType?.toLowerCase().includes('remote')
      );
    }

    return filtered;
  };

  const matchSalaryRange = (jobSalary, filterRange) => {
    const ranges = {
      '$0-50k': [0, 50000],
      '$50k-100k': [50000, 100000],
      '$100k-150k': [100000, 150000],
      '$150k+': [150000, Infinity]
    };
    
    const [min, max] = ranges[filterRange];
    return jobSalary >= min && jobSalary <= max;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchAllJobs = async () => {
      setIsLoading(true);
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
        if (!isMounted) return;

        if (!employeeDoc.exists()) {
          navigate('/role-selection');
          return;
        }

        const employeeData = employeeDoc.data();
        setUserName(employeeData.fullName || '');
        setUserProfilePicture(employeeData.profilePic || 'https://via.placeholder.com/40');

        const applicationsQuery = query(collection(db, 'applications'), where('employeeId', '==', user.uid));
        const declinedJobsQuery = query(collection(db, 'declinedJobs'), where('employeeId', '==', user.uid));
        const savedJobsQuery = query(collection(db, 'savedJobs'), where('employeeId', '==', user.uid));

        const [
          applicationsSnapshot,
          declinedJobsSnapshot,
          savedJobsSnapshot,
          jobsSnapshot,
          adzunaJobs
        ] = await Promise.all([
          getDocs(applicationsQuery),
          getDocs(declinedJobsQuery),
          getDocs(savedJobsQuery),
          getDocs(collection(db, 'jobs')),
          showExternalJobs ? fetchAdzunaJobs(searchQuery) : Promise.resolve([])
        ]);

        if (!isMounted) return;

        const appliedJobIds = new Set(applicationsSnapshot.docs.map(doc => doc.data().jobId));
        const declinedJobIds = new Set(declinedJobsSnapshot.docs.map(doc => doc.data().jobId));
        const savedJobIds = new Set(savedJobsSnapshot.docs.map(doc => doc.data().jobId));

        setAppliedJobs(appliedJobIds);
        setDeclinedJobs(declinedJobIds);
        setSavedJobs(savedJobIds);
        
        const filteredAdzunaJobs = adzunaJobs.filter(job => !appliedJobIds.has(job.id) && !declinedJobIds.has(job.id));
        setExternalJobs(filteredAdzunaJobs);

        let internalJobs = jobsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(job => !appliedJobIds.has(job.id) && !declinedJobIds.has(job.id));

        const employeeKeywords = [
          ...(employeeData.skills || []),
          ...(employeeData.interests || []),
          ...(employeeData.preferredIndustries || []),
          ...(employeeData.languages || []),
        ].map(k => k.toLowerCase());

        const getJobKeywords = (job) => {
            return [
              job.title, job.company, job.industry, job.jobType,
              ...(job.requirements ? job.requirements.split(/[ ,.;\n]+/) : []),
              ...(job.description ? job.description.split(/[ ,.;\n]+/) : []),
            ].filter(Boolean).map(k => k.toLowerCase());
        };

        internalJobs = internalJobs.map(job => {
          const jobKeywords = getJobKeywords(job);
          const matchCount = employeeKeywords.filter((ek, idx, arr) => jobKeywords.includes(ek) && arr.indexOf(ek) === idx).length;
          return { ...job, matchCount };
        }).sort((a, b) => b.matchCount - a.matchCount);

        setJobs(internalJobs);

        const allJobs = [...internalJobs, ...filteredAdzunaJobs];
        setFilteredJobs(filterJobs(allJobs));

      } catch (error) {
        console.error('Error fetching user and job data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAllJobs();

    return () => {
      isMounted = false;
    };
  }, [navigate, showExternalJobs, searchQuery]);

  const handleRightSwipe = async (job) => {
    if (job.isExternal) {
      window.open(job.externalUrl, '_blank');
      setPendingExternalJob(job);
      setShowExternalApplyConfirm(true);
      return;
    }

    // Existing application logic for internal jobs
    const user = auth.currentUser;
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setIsApplying(true);
      setShowMessage(true);
      setShowConfetti(true);
      setPopupMessage("Application Sent!");

      // Store application data
      const currentJob = job;
      // Prevent duplicate applications
      const existingAppQuery = query(
        collection(db, 'applications'),
        where('employeeId', '==', user.uid),
        where('jobId', '==', currentJob.id)
      );
      const existingAppSnapshot = await getDocs(existingAppQuery);
      if (!existingAppSnapshot.empty) {
        setAppliedJobs(prev => new Set([...prev, currentJob.id]));
        setJobs(prev => prev.filter(job => job.id !== currentJob.id));
        setFilteredJobs(prev => prev.filter(job => job.id !== currentJob.id));
        return; // Already applied
      }
      // Add application to applications collection
      const applicationRef = await addDoc(collection(db, 'applications'), {
        employeeId: user.uid,
        jobId: currentJob.id,
        employerId: currentJob.employerId,
        jobTitle: currentJob.title,
        company: currentJob.company,
        appliedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        status: 'pending'
      });

      // Add right swipe to employeeSwipes collection for mutual match logic
      await addDoc(collection(db, 'employeeSwipes'), {
        employeeId: user.uid,
        employerId: currentJob.employerId,
        jobId: currentJob.id,
        direction: 'right',
        swipedAt: new Date()
      });

      // Update job document to include applicant
      const jobRef = doc(db, 'jobs', currentJob.id);
      await updateDoc(jobRef, {
        applicants: arrayUnion(user.uid)
      });

      // Update local state to remove the applied job
      setAppliedJobs(prev => new Set([...prev, currentJob.id]));
      setJobs(prev => prev.filter(job => job.id !== currentJob.id));
      setFilteredJobs(prev => prev.filter(job => job.id !== currentJob.id));

      // Dispatch custom event to notify of new application
      const event = new CustomEvent('newApplicationCreated', {
        detail: {
          applicationId: applicationRef.id,
          jobId: currentJob.id,
          employerId: currentJob.employerId,
          jobTitle: currentJob.title,
          company: currentJob.company,
          createdAt: new Date()
        }
      });
      window.dispatchEvent(event);

    } catch (error) {
      console.error('Error handling match:', error);
    } finally {
      setIsApplying(false);
      setTimeout(() => setFadeOutConfetti(true), 1500);
      setTimeout(() => {
        setShowConfetti(false);
        setFadeOutConfetti(false);
      }, 2000);

      setTimeout(() => setShowMessage(false), 2000);
    }
  };

  const handleLeftSwipe = async () => {
    if (!filteredJobs.length || !filteredJobs[index]) return;

    const currentJob = filteredJobs[index];
    const user = auth.currentUser;

    if (user) {
      try {
        await addDoc(collection(db, 'declinedJobs'), {
          ...currentJob,
          employeeId: user.uid,
          jobId: currentJob.id,
          employerId: currentJob.employerId || null,
          declinedAt: serverTimestamp(),
        });

        setDeclinedJobs(prev => new Set([...prev, currentJob.id]));

        if (currentJob.isExternal) {
          setExternalJobs(prev => prev.filter(job => job.id !== currentJob.id));
        } else {
          setJobs(prev => prev.filter(job => job.id !== currentJob.id));
        }

      } catch (error) {
        console.error('Error storing declined job:', error);
      }
    }
  };

  const handleSwipeComplete = (resetCardPosition) => {
    // Clamp index to valid range after swipe
    setIndex((prevIndex) => {
      const nextIndex = prevIndex + 1;
      return nextIndex >= filteredJobs.length ? 0 : nextIndex;
    });
    setActiveJobCardTab('Description'); // Reset tab to default
    setCompanyDescription('');
    resetCardPosition();
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    
    if (query === '') {
      setFilteredJobs(jobs);
      setIndex(0);
      return;
    }

    const filtered = jobs.filter(job => {
      const searchableText = [
        job.title,
        job.company,
        job.description,
        job.location,
        job.requirements,
        ...(job.reasonsToJoin || [])
      ].join(' ').toLowerCase();

      return searchableText.includes(query);
    });

    setFilteredJobs(filtered);
    setIndex(0);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Utility function to shuffle an array
  function shuffleArray(array) {
    const arr = array.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // Update filtered jobs whenever jobs or filters change
  useEffect(() => {
    let allJobs = showExternalJobs ? [...jobs, ...externalJobs] : [...jobs];
    if (showExternalJobs) {
      allJobs = shuffleArray(allJobs);
    }
    setFilteredJobs(filterJobs(allJobs));
    setIndex(0); // Always reset index to 0 when jobs list changes
  }, [jobs, externalJobs, selectedFilters, showExternalJobs]);

  // Function to fetch employer details and show modal
  const handleEmployerPicClick = async (employerId) => {
    if (!employerId) return;
    try {
      const employerDoc = await getDoc(doc(db, 'employers', employerId));
      if (employerDoc.exists()) {
        setEmployerDetails({ ...employerDoc.data(), id: employerId });
        setEmployerModalTab('overview');
        setShowEmployerModal(true);
      }
    } catch (e) {
      setEmployerDetails(null);
      setShowEmployerModal(false);
    }
  };

  const handleBookmarkClick = async (e, job) => {
    e.stopPropagation();
    const user = auth.currentUser;
    if (!user || !job) return;
    
    try {
      if (savedJobs.has(job.id)) {
        // Un-bookmark: remove from Firestore and local state
        const savedJobsQuery = query(
          collection(db, 'savedJobs'),
          where('employeeId', '==', user.uid),
          where('jobId', '==', job.id)
        );
        const savedJobsSnapshot = await getDocs(savedJobsQuery);
        savedJobsSnapshot.forEach(async (docSnap) => {
          await deleteDoc(docSnap.ref);
        });
        
        // Update local state
        setSavedJobs(prev => {
          const newSet = new Set(prev);
          newSet.delete(job.id);
          return newSet;
        });
      } else {
        // Prevent duplicate saves
        const savedJobsQuery = query(
          collection(db, 'savedJobs'),
          where('employeeId', '==', user.uid),
          where('jobId', '==', job.id)
        );
        const savedJobsSnapshot = await getDocs(savedJobsQuery);
        if (!savedJobsSnapshot.empty) return; // Already saved

        // Save to Firestore
        const savedJobRef = doc(collection(db, 'savedJobs'));
        const saveData = {
            ...job, // copy all fields from job
            employeeId: user.uid,
            savedAt: new Date(),
            employerId: job.employerId || null, // ensure employerId is not undefined
            jobId: job.id, // Add this line to ensure jobId is always present
        };
        await setDoc(savedJobRef, saveData);

        // Update local state
        setSavedJobs(prev => new Set([...prev, job.id]));
        setBookmarkPop(true);
        setTimeout(() => setBookmarkPop(false), 600); // Reset after animation
      }
    } catch (error) {
      console.error('Error handling bookmark:', error);
    }
  };

  // Fetch jobs for employer when modal opens and tab is 'jobs'
  useEffect(() => {
    const fetchEmployerJobs = async () => {
      if (showEmployerModal && employerDetails?.id) {
        const jobsQuery = query(
          collection(db, 'jobs'),
          where('employerId', '==', employerDetails.id),
          where('status', '==', 'active')
        );
        const jobsSnapshot = await getDocs(jobsQuery);
        setEmployerJobs(jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    };
    if (employerModalTab === 'jobs') {
      fetchEmployerJobs();
    }
  }, [showEmployerModal, employerDetails, employerModalTab]);

  // Add this new useEffect to fetch external jobs when toggle is enabled
  useEffect(() => {
    const loadExternalJobs = async () => {
      if (showExternalJobs) {
        setIsLoadingExternal(true);
        try {
          const adzunaJobs = await fetchAdzunaJobs(searchQuery);
          // Filter out jobs that have been applied to or declined
          const filteredAdzunaJobs = adzunaJobs.filter(job => 
            !appliedJobs.has(job.id) && !declinedJobs.has(job.id)
          );
          setExternalJobs(filteredAdzunaJobs);
          // Immediately update filteredJobs to include new external jobs
          const allJobs = [...jobs, ...filteredAdzunaJobs];
          setFilteredJobs(filterJobs(allJobs));
        } catch (error) {
          console.error('Error loading external jobs:', error);
        } finally {
          setIsLoadingExternal(false);
        }
      } else {
        setExternalJobs([]);
        setFilteredJobs(filterJobs(jobs));
      }
    };

    loadExternalJobs();
  }, [showExternalJobs, searchQuery, jobs, appliedJobs, declinedJobs]);

  // Save listMode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('listMode', listMode);
  }, [listMode]);

  // Save showExternalJobs to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('showExternalJobs', showExternalJobs);
  }, [showExternalJobs]);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
        if (activeJobCardTab === 'About' && filteredJobs.length > 0 && index < filteredJobs.length) {
            const currentJob = filteredJobs[index];
            if (!currentJob || !currentJob.employerId) {
                setCompanyDescription(`More information about ${currentJob.company} is not available.`);
                return;
            }

            // Check if we already have the details
            if (employerDetails && employerDetails.id === currentJob.employerId) {
                 setCompanyDescription(employerDetails.about || 'No description provided.');
                 return;
            }

            setIsCompanyInfoLoading(true);
            try {
                const employerDoc = await getDoc(doc(db, 'employers', currentJob.employerId));
                if (employerDoc.exists()) {
                    const employerData = employerDoc.data();
                    setCompanyDescription(employerData.about || 'No detailed company description available.');
                    // also set it to the main employer details in case user clicks profile
                    setEmployerDetails({ ...employerData, id: currentJob.employerId });
                } else {
                    setCompanyDescription('Company information could not be found.');
                }
            } catch (error) {
                console.error('Error fetching company info:', error);
                setCompanyDescription('Could not load company information.');
            } finally {
                setIsCompanyInfoLoading(false);
            }
        }
    };

    fetchCompanyInfo();
  }, [activeJobCardTab, index, filteredJobs, employerDetails]);

  const handleExternalApplyConfirm = async (didApply) => {
    setShowExternalApplyConfirm(false);
    if (didApply && pendingExternalJob) {
      const user = auth.currentUser;
      if (!user) return;
      // Debug log to inspect job object
      console.log('DEBUG: pendingExternalJob', pendingExternalJob);
      // Robust extraction for job title and company
      const jobTitle = pendingExternalJob.title || pendingExternalJob.jobTitle || (pendingExternalJob.job && pendingExternalJob.job.title) || 'Unknown Job';
      let company = pendingExternalJob.company;
      if (typeof company === 'object' && company !== null) {
        company = company.display_name || company.name || JSON.stringify(company);
      }
      company = company || pendingExternalJob.company_name || (pendingExternalJob.job && (pendingExternalJob.job.company || pendingExternalJob.job.company_name)) || 'Unknown Company';
      try {
        await addDoc(collection(db, 'applications'), {
          employeeId: user.uid,
          jobId: pendingExternalJob.id,
          employerId: pendingExternalJob.employerId || null,
          jobTitle,
          company,
          appliedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          status: 'external-applied',
          isExternal: true,
          externalUrl: pendingExternalJob.externalUrl
        });
        setAppliedJobs(prev => new Set([...prev, pendingExternalJob.id]));
        // Remove from external jobs and filtered jobs
        setExternalJobs(prev => prev.filter(job => job.id !== pendingExternalJob.id));
        setFilteredJobs(prev => prev.filter(job => job.id !== pendingExternalJob.id));
      } catch (error) {
        console.error('Error saving external application:', error);
      }
    }
    setPendingExternalJob(null);
    setIndex((prevIndex) => (prevIndex + 1) % filteredJobs.length);
  };

  const handleViewSimilarRoles = async () => {
    const currentJob = filteredJobs[index];
    if (!currentJob) return;

    // For external jobs, we might not have an employerId in our DB
    // A more robust solution would be to find jobs with the same company name
    if (!currentJob.employerId && !currentJob.isExternal) {
      console.log('No employer ID found for current internal job');
      return;
    }

    try {
      let similarJobsList = [];

      if (currentJob.isExternal) {
        // For external jobs, we can search all jobs by company name
        const allJobsSnapshot = await getDocs(collection(db, 'jobs'));
        const allJobs = allJobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        similarJobsList = allJobs
          .filter(job => 
            job.company.toLowerCase() === currentJob.company.toLowerCase() &&
            job.id !== currentJob.id
          );
      } else {
        // For internal jobs, query by employerId and optionally industry
        const constraints = [where('employerId', '==', currentJob.employerId)];
        if (currentJob.industry) {
          constraints.push(where('industry', '==', currentJob.industry));
        } else {
          console.log(`Industry not specified for job: ${currentJob.title}. Searching by employer only.`);
        }
        
        const similarJobsQuery = query(collection(db, 'jobs'), ...constraints);
        const similarJobsSnapshot = await getDocs(similarJobsQuery);
        
        similarJobsList = similarJobsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(job => job.id !== currentJob.id); // Exclude the current job
      }

      setSimilarJobs(similarJobsList);
      setShowSimilarRoles(true);
    } catch (error) {
      console.error('Error fetching similar jobs:', error);
    }
  };

  return (
    <>
      <div className="main-content">
        <div className="header">
          <div className="search-section">
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={handleSearch}
              className="search-input"
            />
            <div className="filters-row">
              {filterTypes.map(({ type, label }) => (
                <button
                  key={type}
                  className={`filter-button ${activeFilter === type ? 'active' : ''} ${selectedFilters[type] ? 'selected' : ''}`}
                  onClick={() => handleFilterClick(type)}
                >
                  {selectedFilters[type] || label}
                  {selectedFilters[type] && (
                    <span className="clear-filter" onClick={(e) => {
                      e.stopPropagation();
                      clearFilter(type);
                    }}>×</span>
                  )}
                </button>
              ))}
            </div>
            {activeFilter && (
              <div className="filter-dropdown">
                {filterOptions[activeFilter].map((option) => (
                  <div
                    key={option}
                    className={`filter-option ${selectedFilters[activeFilter] === option ? 'selected' : ''}`}
                    onClick={() => handleFilterSelect(activeFilter, option)}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="profile-section">
            {userProfilePicture ? (
              <img
                src={userProfilePicture}
                alt="Profile"
                className="profile-picture"
                onClick={() => navigate('/profile')}
              />
            ) : (
              <LetterAvatar 
                name={userName || 'Unknown'} 
                size={90}
                className="profile-picture"
                onClick={() => navigate('/profile')}
              />
            )}
          </div>
        </div>
        <div className="job-feed" style={{ marginBottom: 80 }}>
          <div className="card-container">
            {listMode ? (
              <div className="indeed-list-mode">
                {filteredJobs.length > 0 ? (
                  filteredJobs.map((job, i) => (
                    <div
                      key={job.id}
                      className="indeed-list-card"
                      onClick={() => {
                        setIndex(i);
                        setListMode(false);
                      }}
                    >
                      <div className="indeed-list-title-row">
                        <div className="indeed-list-title">{job.title}</div>
                        <div className="indeed-list-company">{job.company}</div>
                      </div>
                      <div className="indeed-list-meta-row">
                        <span className="indeed-list-salary">{job.salary || 'N/A'}</span>
                        <span className="indeed-list-location">{job.location || 'N/A'}</span>
                      </div>
                      <div className="indeed-list-benefits-row">
                        {/* Optionally show perks, etc. */}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">💼</div>
                    <div className="empty-state-title">No Jobs Found</div>
                    <div className="empty-state-message">
                      No jobs found matching your search. Try adjusting your search or check back later for new opportunities!
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <DraggableCard 
                onSwipeComplete={handleSwipeComplete} 
                onRightSwipe={() => handleRightSwipe(filteredJobs[index])}
                onLeftSwipe={handleLeftSwipe}
                className={!filteredJobs.length ? 'empty-card' : ''}
              >
                {filteredJobs.length > 0 ? (
                  <div className="job-card-grid">
                    {/* Top Row: Logo and Title/Company */}
                    <div className="job-card-grid-row top-row">
                      <div className="job-card-logo-box" style={{ overflow: 'hidden', padding: 0, border: 'none', cursor: 'pointer' }}
                        onClick={() => handleEmployerPicClick(filteredJobs[index]?.employerId)}
                      >
                        {filteredJobs[index]?.profilePic ? (
                          <img
                            src={filteredJobs[index].profilePic}
                            alt={`${filteredJobs[index]?.company} profile`}
                            className="job-logo-full"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <LetterAvatar 
                            name={filteredJobs[index]?.company || 'Unknown Company'} 
                            size={90}
                            className="job-logo-square"
                            style={{ borderRadius: '16px 0 0 16px', width: '100%', height: '100%' }}
                          />
                        )}
                      </div>
                      <div className="job-card-title-company-box">
                        <FaBookmark 
                          className={`job-card-bookmark-tag${bookmarkPop ? ' pop' : ''}`}
                          onClick={e => handleBookmarkClick(e, filteredJobs[index])}
                          style={{
                            opacity: 1,
                            color: savedJobs.has(filteredJobs[index]?.id) ? "#D65A31" : "#fff",
                            fill: savedJobs.has(filteredJobs[index]?.id) ? "#D65A31" : "#fff"
                          }}
                        />
                        <div className="job-card-title">{filteredJobs[index]?.title}</div>
                        <div className="job-card-company">{filteredJobs[index]?.company}</div>
                      </div>
                    </div>
                    {/* Second Row: Salary and Location */}
                    <div className="job-card-grid-row salary-location-row">
                      <div className="job-card-salary-dark-box">
                        <div className="job-card-label">Salary:</div>
                        <div className="job-card-value">{filteredJobs[index]?.salary || 'N/A'}</div>
                      </div>
                      <div className="job-card-location-dark-box">
                        <div className="job-card-label">Location</div>
                        <div className="job-card-value">{filteredJobs[index]?.location || 'N/A'}</div>
                      </div>
                    </div>
                    {/* Tabs */}
                    <div className="job-card-tabs">
                      <button className={`job-card-tab ${activeJobCardTab === 'Description' ? 'active' : ''}`} onClick={() => setActiveJobCardTab('Description')}>Description</button>
                      <button className={`job-card-tab ${activeJobCardTab === 'Requirements' ? 'active' : ''}`} onClick={() => setActiveJobCardTab('Requirements')}>Requirements</button>
                      <button className={`job-card-tab ${activeJobCardTab === 'About' ? 'active' : ''}`} onClick={() => setActiveJobCardTab('About')}>About</button>
                    </div>
                    {/* Tab Content */}
                    <div className="job-card-tab-content">
                      {activeJobCardTab === 'Description' && (
                        <div>{filteredJobs[index]?.description || 'No description provided.'}</div>
                      )}
                      {activeJobCardTab === 'Requirements' && (
                        <div>
                          {filteredJobs[index]?.requirements && filteredJobs[index].requirements.trim() !== ''
                            ? filteredJobs[index].requirements.split('\n').map((req, i) => <div key={i}>{req}</div>)
                            : 'No requirements listed.'}
                        </div>
                      )}
                      {activeJobCardTab === 'About' && (
                        <div>
                          {isCompanyInfoLoading ? (
                            <p>Loading company info...</p>
                          ) : (
                            <p>{companyDescription}</p>
                          )}
                          <button 
                            className="view-similar-roles-btn"
                            onClick={handleViewSimilarRoles}
                          >
                            View Similar Roles
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">💼</div>
                    <div className="empty-state-title">No Jobs Found</div>
                    <div className="empty-state-message">
                      No jobs found matching your search. Try adjusting your search or check back later for new opportunities!
                    </div>
                  </div>
                )}
              </DraggableCard>
            )}
          </div>
        </div>
      </div>
      {/* Fixed toggle switches at the bottom of the screen */}
      <div className="toggle-switches toggle-switches-fixed">
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={showExternalJobs}
            onChange={e => {
              setShowExternalJobs(e.target.checked);
              if (!e.target.checked) {
                setExternalJobs([]);
                setFilteredJobs(jobs); // Instantly update the list to only internal jobs
                setIndex(0);
              }
            }}
          />
          <span className="toggle-slider"></span>
          <span className="toggle-label">Show External Postings</span>
        </label>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={listMode}
            onChange={e => setListMode(e.target.checked)}
          />
          <span className="toggle-slider"></span>
          <span className="toggle-label">List Mode</span>
        </label>
      </div>
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          numberOfPieces={150}
          recycle={false}
          style={{
            opacity: fadeOutConfetti ? 0 : 1,
            transition: 'opacity 0.5s ease-out',
          }}
        />
      )}
      {showMessage && (
        <div className="application-message">
          {popupMessage}
        </div>
      )}
      {showDescriptionModal && modalJob && (
        <div className="modal-overlay" onClick={() => setShowDescriptionModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => setShowDescriptionModal(false)}>&times;</button>
            <div className="modal-header">
              <h3 className="modal-title">Job Details</h3>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <h4 className="modal-section-title">Description</h4>
                <div className="modal-section-content">{modalJob.description}</div>
              </div>
              <div className="modal-section">
                <h4 className="modal-section-title">Requirements</h4>
                <div className="modal-section-content">
                  {modalJob.requirements && modalJob.requirements.trim() !== ''
                    ? modalJob.requirements.split('\n').map((req, i) => <div key={i}>{req}</div>)
                    : 'No requirements listed.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showExternalApplyConfirm && pendingExternalJob && (
        <div className="modal-overlay" onClick={() => handleExternalApplyConfirm(false)}>
          <div className="modal-content external-apply-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => handleExternalApplyConfirm(false)}>&times;</button>
            <div className="modal-header">
              <h3 className="modal-title">Did you apply?</h3>
              <div className="job-info">
                <div className="job-title">{pendingExternalJob.title || 'Job Title'}</div>
                <div className="company-name">{pendingExternalJob.company || 'Company'}</div>
              </div>
            </div>
            <div className="modal-body">
              <p>Let us know if you completed your application on the external site.</p>
              <button className="external-apply-btn" onClick={() => handleExternalApplyConfirm(true)}>Yes</button>
              <button className="external-apply-btn secondary" onClick={() => handleExternalApplyConfirm(false)}>No</button>
            </div>
          </div>
        </div>
      )}
      {showSimilarRoles && (
        <div className="modal-overlay" onClick={() => setShowSimilarRoles(false)}>
          <div className="modal-content similar-roles-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => setShowSimilarRoles(false)}>&times;</button>
            <div className="modal-header">
              <h3 className="modal-title">Similar Roles at {filteredJobs[index]?.company}</h3>
            </div>
            <div className="modal-body">
              {similarJobs.length > 0 ? (
                <div className="similar-jobs-list">
                  {similarJobs.map((job) => (
                    <div key={job.id} className="similar-job-item">
                      <div className="similar-job-title">{job.title}</div>
                      <div className="similar-job-details">
                        <span className="similar-job-salary">{job.salary || 'Salary not specified'}</span>
                        <span className="similar-job-location">{job.location || 'Location not specified'}</span>
                      </div>
                      <div className="similar-job-description">
                        {job.description ? job.description.substring(0, 150) + '...' : 'No description available'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-similar-jobs">
                  <p>No similar roles found at this company.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeFeed;